import { ReactElement, useEffect, useRef, useState } from 'react';

type LazyMountProps = {
  /** Render the children right away (rows of the first screen). */
  eager: boolean;
  /** Shown until the children are rendered (a skeleton of about the same height). */
  placeholder: ReactElement;
  children: ReactElement;
};

/**
 * How far below (or above) the viewport a placeholder starts rendering its row.
 * The horizontal margin is effectively unlimited: on narrow screens the size
 * grid scrolls the page sideways, and a placeholder left of the viewport must
 * still count as "near" — only the vertical distance matters.
 */
const ROOT_MARGIN = '1500px 100000px';

/**
 * Renders its children only once they come near the viewport; until then the
 * `placeholder` keeps the page height (and scrollbar) close to the real one.
 * Once rendered the children stay mounted, so form state of rows the user
 * scrolled past is kept. A 1,000-line order used to
 * mount every row (~60k DOM nodes, ~10k inputs) before showing anything.
 * Without IntersectionObserver (old browsers, tests) everything renders at once.
 */
export function LazyMount({ eager, placeholder, children }: LazyMountProps): ReactElement {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(
    () => eager || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    const node = placeholderRef.current;
    if (isMounted || !node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMounted]);

  if (isMounted) return children;

  return <div ref={placeholderRef}>{placeholder}</div>;
}
